'use client'

import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { Sparkles } from './sparkles'

// Define screen size map similar to 3d-carousel
const SCREEN_SIZE_MAP: Record<ScreenSize, number> = {
  '2xl': 35,
  xl: 35,
  lg: 35,
  md: 45,
  sm: 60,
  xs: 80,
}

// Helper to generate a time-saving string based on the card title
function getTimeSaved(title: string): string {
  // Simple heuristics for demo purposes
  if (/tax|pdf|legal|policy|analy/i.test(title)) return 'Saves ~5h/week'
  if (/cover letter|interview/i.test(title)) return 'Saves ~2h/job'
  if (/ads|promo|mockup|infographic|brand|moodboard/i.test(title)) return 'Saves ~3h/week'
  if (/podcast|script/i.test(title)) return 'Saves ~6h/episode'
  if (/tweet|linkedin/i.test(title)) return 'Saves ~30min/post'
  return 'Saves ~1h/week'
}

// Separate card component to handle individual hover states
const Card = ({
  item,
  isLoading,
  onStartClick,
}: {
  item: {
    title: string
    src: string
    agentId: string
  }
  isLoading: boolean
  onStartClick?: (agentId: string, title: string) => void
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const screenSize = useScreenSize()
  const timeSaved = getTimeSaved(item.title)

  return (
    <li
      className="relative max-w-full shrink-0 cursor-pointer select-none overflow-clip rounded-[20px]"
      style={{
        width: `${SCREEN_SIZE_MAP[screenSize]}vmin`,
        height: `${SCREEN_SIZE_MAP[screenSize]}vmin`,
        transform: `scale(${isHovered ? 1.05 : 1})`,
        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isHovered
          ? '0 0 25px 5px rgba(255, 255, 255, 0.3), 0 0 10px 1px rgba(109, 100, 247, 0.5)'
          : 'none',
      }}
      onClick={() => onStartClick?.(item.agentId, item.title)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Time saved tag */}
      <div
        className="pointer-events-none absolute right-3 top-3 z-20 select-none rounded-full bg-black/60 px-4 py-1 shadow-md backdrop-blur"
        style={{
          color: '#FFF',
          fontFamily: 'Space Grotesk',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '110%',
        }}
      >
        {timeSaved}
      </div>
      {isHovered && <Sparkles active={true} isHovered={isHovered} />}

      <button className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-[1%] bg-[#1D1F2F]">
        <Image
          className="absolute inset-0 h-[120%] w-[120%] select-none object-cover opacity-100 [-webkit-user-drag:none] [user-drag:none]"
          alt={item.title}
          src={item.src}
          width={500}
          height={500}
          loading="eager"
          priority
          unoptimized={true}
          draggable={false}
        />
        <div className="via-black/24 absolute inset-0 bg-gradient-to-b from-transparent via-35% to-black/50" />
      </button>

      <article className="relative flex h-full flex-col justify-end p-[2vmin]">
        <div className="flex-grow"></div>
        <h2
          className="!font-libre-baskerville relative mb-3 bg-gradient-to-b from-white/50 via-white to-white/50 bg-clip-text text-left leading-[1em] text-transparent"
          style={{
            fontSize: `${SCREEN_SIZE_MAP[screenSize] * 0.11}vmin`, // About 11% of card size
          }}
        >
          {item.title}
        </h2>
        <button
          className="font-space-grotesk mt-auto flex items-center gap-2 self-start rounded-[2rem] border border-white/20 bg-black/30 px-4 py-[0.4rem] text-[1em] text-white/90 backdrop-blur hover:bg-black/40"
          style={{
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: isLoading ? '44px' : 'auto',
            minWidth: isLoading ? '44px' : '120px',
            height: '36px',
            marginBottom: '0.5rem',
          }}
          onClick={(e) => {
            e.stopPropagation()
            onStartClick?.(item.agentId, item.title)
          }}
          disabled={isLoading}
        >
          <div className="flex w-full items-center justify-center">
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="h-4 w-4 animate-spin text-white/70"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
            ) : (
              <span className="flex items-center transition-opacity duration-300 ease-in-out">
                Start with template
                <span className="ml-2 inline-block pt-[0.2em] text-[0.75em]">→</span>
              </span>
            )}
          </div>
        </button>
      </article>
    </li>
  )
}

export const InfiniteMovingCards = ({
  items,
  direction = 'left',
  speed = 'fast',
  pauseOnHover = true,
  className,
  isLoading = false,
  onStartClick,
}: {
  items: {
    title: string
    src: string
    agentId: string
  }[]
  direction?: 'left' | 'right'
  speed?: 'fast' | 'normal' | 'slow'
  pauseOnHover?: boolean
  className?: string
  isLoading?: boolean
  onStartClick?: (agentId: string, title: string) => void
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const scrollerRef = React.useRef<HTMLUListElement>(null)
  const [start, setStart] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [animationDuration, setAnimationDuration] = useState('40s')
  const [animationDirection, setAnimationDirection] = useState('forwards')

  useEffect(() => {
    // Set direction and speed immediately
    if (direction === 'left') {
      setAnimationDirection('forwards')
    } else {
      setAnimationDirection('reverse')
    }

    if (speed === 'fast') {
      setAnimationDuration('20s')
    } else if (speed === 'normal') {
      setAnimationDuration('40s')
    } else {
      setAnimationDuration('80s')
    }

    // Use a longer delay to ensure DOM is fully rendered in production
    const timer = setTimeout(() => {
      setStart(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [direction, speed])

  const handleMouseEnter = () => {
    if (pauseOnHover) {
      setIsPaused(true)
    }
  }

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      setIsPaused(false)
    }
  }

  return (
    <div
      ref={containerRef}
      className={cn('scroller relative z-20 max-w-full overflow-hidden', className)}
    >
      <ul
        ref={scrollerRef}
        className="flex w-max min-w-full shrink-0 flex-nowrap gap-4 py-4"
        style={{
          animationName: start ? 'scroll' : 'none',
          animationDuration,
          animationDirection,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationPlayState: isPaused ? 'paused' : 'running',
          transform: start ? undefined : 'translateX(0)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex gap-4">
          {/* Render the items once */}
          {items.map((item, idx) => (
            <Card
              key={`${item.title}-${idx}-original`}
              item={item}
              isLoading={isLoading}
              onStartClick={onStartClick}
            />
          ))}
        </div>

        <div className="flex gap-4">
          {/* Render the same items again for the infinite scroll effect */}
          {items.map((item, idx) => (
            <Card
              key={`${item.title}-${idx}-duplicate`}
              item={item}
              isLoading={isLoading}
              onStartClick={onStartClick}
            />
          ))}
        </div>
      </ul>
    </div>
  )
}
